import { camelCase, startCase } from 'lodash';

import {
  Appraisal as GqlAppraisal,
  AppraisalMethod as GqlAppraisalMethod,
  AppraisalType as GqlAppraisalType,
} from '@willow/graphql-iso/src/app';
import {
  Appraisal as IsoAppraisal,
  AppraisalMethod,
  AppraisalType,
  assertNever,
  jsDateTimeToTemporalPlainDate,
  parseGqlIsoDate,
  parseGqlIsoDateTime,
  PropertyId,
  Temporal,
  toTemporalInstant,
} from '@willow/types-iso';

export function getAppraisalTypeLabel(appraisalType: AppraisalType | GqlAppraisalType): string {
  switch (appraisalType) {
    case 'appraisal':
    case GqlAppraisalType.Appraisal:
      return startCase(camelCase(appraisalType));
    case 'bpo':
    case GqlAppraisalType.Bpo:
      return 'BPO';
    default:
      assertNever(appraisalType);
  }
}

export function getAppraisalMethodLabel(appraisalMethod: AppraisalMethod | GqlAppraisalMethod): string {
  return startCase(camelCase(appraisalMethod));
}

export const getOriginalAppraisalForProperty = <T extends IsoAppraisal | GqlAppraisal>(
  propertyId: PropertyId,
  appraisals: T[],
): T | undefined => {
  const appraisalsForProperty = appraisals.filter((a) => a.propertyId === propertyId);
  const originalAppraisals = appraisalsForProperty.filter((a) => a.isOriginal);
  switch (originalAppraisals.length) {
    case 0:
      // TODO delete after migration is run: an appraisal with `isOriginal` should always be present
      // https://github.com/willowservicing/willow/issues/9097
      // handle case of `isOriginal` not computed - compute original appraisal
      return computeOriginalAppraisalForProperty(appraisalsForProperty);
    case 1:
      return originalAppraisals.at(0)!;
    default:
      throw new Error('Multiple original appraisals found for property');
  }
};

/**
 * Computes the original appraisal for a list of appraisals associated
 * with a particular property.
 *
 * The original appraisal is the most recently updated (or created)
 * appraisal on the earliest appraisal date.
 */
export const computeOriginalAppraisalForProperty = <T extends IsoAppraisal | GqlAppraisal>(
  appraisalsForProperty: T[],
): T | undefined =>
  // toSorted not supported in investor portal because of outdated ES version...
  [...appraisalsForProperty]
    .sort((a, b) => {
      const aPlainDate = jsDateTimeToTemporalPlainDate(parseGqlIsoDate(a.date), 'UTC');
      const bPlainDate = jsDateTimeToTemporalPlainDate(parseGqlIsoDate(b.date), 'UTC');

      const result = Temporal.PlainDate.compare(aPlainDate, bPlainDate);
      if (result !== 0) {
        return result;
      }
      const aUpdatedAtInstant = toTemporalInstant(
        a.updatedAt instanceof Date ? a.updatedAt : parseGqlIsoDateTime(a.updatedAt),
      );
      const bUpdatedAtInstant = toTemporalInstant(
        b.updatedAt instanceof Date ? b.updatedAt : parseGqlIsoDateTime(b.updatedAt),
      );
      return Temporal.Instant.compare(bUpdatedAtInstant, aUpdatedAtInstant);
    })
    .at(0);
